import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LookupService } from '../../_services/lookup.service';
import { Lookup } from '../../_model/Lookup';

@Component({
  selector: 'app-reject-reason-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatRadioModule,
    MatProgressSpinnerModule
  ],
  template: `
    <h2 mat-dialog-title>Reddetme Nedeni</h2>
    <mat-dialog-content>
      <div *ngIf="loading" style="text-align: center; padding: 20px;">
        <mat-spinner diameter="40"></mat-spinner>
        <p style="margin-top: 10px; font-size: 13px;">Yükleniyor...</p>
      </div>
      
      <form [formGroup]="rejectForm" *ngIf="!loading">
        <div class="reason-selection">
          <mat-radio-group formControlName="reasonType">
            <mat-radio-button *ngFor="let reason of rejectReasons" [value]="reason.id">
              {{reason.description}}
            </mat-radio-button>
          </mat-radio-group>
        </div>

        <mat-form-field class="full-width" *ngIf="isOtherReasonSelected()">
          <mat-label>Açıklama *</mat-label>
          <textarea matInput 
                    formControlName="description" 
                    rows="4" 
                    placeholder="Red nedeninizi açıklayınız..."></textarea>
          <mat-error *ngIf="rejectForm.get('description')?.hasError('required')">
            Açıklama zorunludur
          </mat-error>
        </mat-form-field>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">İptal</button>
      <button mat-raised-button color="warn" (click)="onConfirm()" [disabled]="!isFormValid() || loading">
        Reddet
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 {
      font-size: 16px;
      margin: 0;
      padding: 16px 24px;
      background-color: #fafafa;
      border-bottom: 1px solid #e0e0e0;
    }

    mat-dialog-content {
      padding: 24px;
      min-width: 450px;
    }

    .reason-selection {
      margin-bottom: 20px;
    }

    mat-radio-group {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    mat-radio-button {
      font-size: 13px;
    }

    .full-width {
      width: 100%;
      margin-top: 16px;
    }

    mat-dialog-actions {
      padding: 16px 24px;
      border-top: 1px solid #e0e0e0;
    }

    ::ng-deep .mat-mdc-radio-button .mdc-label {
      font-size: 13px;
    }
  `]
})
export class RejectReasonDialogComponent implements OnInit {
  rejectForm: FormGroup;
  rejectReasons: Lookup[] = [];
  loading = true;
  otherReasonId: number | null = null; // "Diğer" seçeneğinin ID'si

  constructor(
    private fb: FormBuilder,
    private lookupService: LookupService,
    public dialogRef: MatDialogRef<RejectReasonDialogComponent>
  ) {
    this.rejectForm = this.fb.group({
      reasonType: [null, Validators.required],
      description: ['']
    });

    // Reason type değiştiğinde "Diğer" seçiliyse description zorunlu yap
    this.rejectForm.get('reasonType')?.valueChanges.subscribe(value => {
      const descControl = this.rejectForm.get('description');
      if (this.isOtherReasonSelected()) {
        descControl?.setValidators([Validators.required]);
      } else {
        descControl?.clearValidators();
      }
      descControl?.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    this.loadRejectReasons();
  }

  loadRejectReasons(): void {
    this.lookupService.listByLookupCode('RECORD_REJECT_REASON').subscribe({
      next: (response) => {
        if (response.success && response.responseData.items) {
          this.rejectReasons = response.responseData.items.sort((a, b) => 
            (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          
          // "Diğer" seçeneğini bul (description'da "Diğer" veya "Other" içeren)
          const otherReason = this.rejectReasons.find(r => 
            r.description.toLowerCase().includes('diğer') || 
            r.description.toLowerCase().includes('other')
          );
          this.otherReasonId = otherReason?.id || null;
          
          // İlk seçeneği varsayılan olarak seç
          if (this.rejectReasons.length > 0) {
            this.rejectForm.patchValue({ reasonType: this.rejectReasons[0].id });
          }
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading reject reasons:', error);
        alert('Red nedenleri yüklenirken hata oluştu!');
        this.loading = false;
      }
    });
  }

  isOtherReasonSelected(): boolean {
    const selectedId = this.rejectForm.get('reasonType')?.value;
    return this.otherReasonId !== null && selectedId === this.otherReasonId;
  }

  isFormValid(): boolean {
    if (this.isOtherReasonSelected()) {
      const description = this.rejectForm.get('description')?.value;
      return description && description.trim() !== '';
    }
    return this.rejectForm.get('reasonType')?.value !== null;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (!this.isFormValid()) {
      return;
    }

    const reasonId = this.rejectForm.get('reasonType')?.value;
    const description = this.rejectForm.get('description')?.value;
    
    const selectedReason = this.rejectReasons.find(r => r.id === reasonId);
    if (!selectedReason) {
      alert('Geçerli bir red nedeni seçiniz!');
      return;
    }

    let rejectDescription = selectedReason.description;
    
    // "Diğer" seçiliyse kullanıcının girdiği açıklamayı ekle
    if (this.isOtherReasonSelected() && description && description.trim() !== '') {
      rejectDescription = selectedReason.description + ': ' + description.trim();
    }

    const result = {
      rejectReasonId: reasonId,
      rejectDescription: rejectDescription
    };

    this.dialogRef.close(result);
  }
}
